import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  ModalFooter,
  Button,
  Input,
  Label,
} from 'reactstrap';

import {
  deleteCollectionFromCollectionsList,
  removeCollectionFromTree,
  removeSidebarCollection,
} from '../../actions/Collections';

import { APIgetCollectionsSummary, APIrestoreCollections } from '../../api/Collections';

import { addFlashMessage } from '../../helpers/FlashMessages';

import ErrorBoundary from '../ErrorBoundary';
import CollectionsTree from './Tree';
import CollectionSelectCollectionMessage from './SelectCollectionMessage';
import ProcessingSpinner from '../ProcessingSpinner';

const CollectionRestoreModal = ({
  handleClose,
  restoringCollections,
  initialParentUuid,
  dispatch,
}) => {
  const intl = useIntl();

  const [parentCollectionUuid, setParentCollectionUuid] = useState(initialParentUuid || 'all');
  const [summary, setSummary] = useState({ files: 0, collections: 0 });
  const [restoreFilesToggle, setRestoreFilesToggle] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRemoveFilesChange = (e) => {
    setRestoreFilesToggle(e.target.checked);
  };

  const handleRestoreCollection = () => {
    setLoading(true);
    return APIrestoreCollections({
      collectionUuidList: restoringCollections,
      parentCollectionUuid,
      withFiles: restoreFilesToggle,
    }).then(() => {
      restoringCollections?.forEach((collectionUuid) => {
        dispatch(removeSidebarCollection(collectionUuid));
        dispatch(deleteCollectionFromCollectionsList({ uuid: collectionUuid }));
        dispatch(removeCollectionFromTree(collectionUuid, 'sidebar'));
      });
      setLoading(false);
      handleClose();
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({
          defaultMessage: 'Collection has been restored',
        }),
      });
    });
  };

  useEffect(() => {
    const fetchCollectionsSummary = async () => {
      setLoading(true);
      try {
        const response = await APIgetCollectionsSummary({
          collectionUuidList: restoringCollections,
          trashed: true,
        });

        if (response.status === 200) {
          setSummary(response.data);
        }
      } catch (_) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionsSummary();
  }, [restoringCollections]);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Restore Collection' })}
      </ModalHeader>
      <ModalBody style={{ overflow: 'visible' }}>
        <Form className="select-collection-form">
          <FormGroup>
            <CollectionSelectCollectionMessage />
            <CollectionsTree
              selectedCollections={parentCollectionUuid ? [parentCollectionUuid] : []}
              enableMyCollections={false}
              enableAddNew={true}
              enableAllCollections={true}
              onlyOpenCollections={true}
              multiSelect={false}
              onChange={(selectedUuids) => {
                if (selectedUuids.length > 0) {
                  setParentCollectionUuid(selectedUuids[0]);
                } else {
                  setParentCollectionUuid('all');
                }
              }}
              smartCollectionsRestrict={false}
              showSmartSection={false}
            />
          </FormGroup>
          {(summary?.files > 0 || summary?.collections > 0) && (
            <span className="summary">
              {intl.formatMessage({
                defaultMessage: `<strong>{collectionsCount}</strong>
                  {collectionsCount, plural, one {Collection} other {Collections}} and
                  <strong>{filesCount}</strong> {filesCount, plural, one {file} other {files}} can be restored`,
              }, {
                collectionsCount: summary.collections,
                filesCount: summary.files,
                strong: (msg) => <strong>{msg}</strong>,
              })}
            </span>
          )}
          {summary?.files > 0 && (
            <FormGroup>
              <Label for="removeFiles" className="switch d-flex mt-3">
                <div className="switch-control input-inner">
                  <Input
                    type="checkbox"
                    id="removeFiles"
                    className="input-check check-s form-check-input no-margin"
                    value={true}
                    disabled={loading}
                    onClick={handleRemoveFilesChange}
                  />
                  <i className="mr-2" />
                </div>
                <div className="switch-details">
                  <span>{intl.formatMessage({ defaultMessage: 'Also restore files' })}</span>
                </div>
              </Label>
            </FormGroup>
          )}
        </Form>
        {error && (
          <p className="error">
            {intl.formatMessage({
              defaultMessage: 'Something went wrong. Please try again or contact us.',
            })}
          </p>
        )}
      </ModalBody>
      <ModalFooter className="justify-r">
        <div className="buttons-group">
          <Button
            color="link"
            disabled={loading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={loading}
            onClick={() => handleRestoreCollection()}
          >
            <ProcessingSpinner show={loading} />
            {intl.formatMessage({ defaultMessage: 'Restore Collection' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

CollectionRestoreModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  restoringCollections: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialParentUuid: PropTypes.string,
};

CollectionRestoreModal.defaultProps = {
  initialParentUuid: null,
};

export default connect()(CollectionRestoreModal);
